/**
 * NCC: <ncc-product-tabs> - the tab strip of sections/ncc-product-tabs.liquid.
 * Plain WAI-ARIA tabs: click or arrow keys move between tabs, the matching panel is un-hidden and
 * the others hidden (and inert - the panels crossfade in CSS, so hidden ones stay laid out). Until this
 * runs, the server-rendered `hidden` attributes and the `:not([data-ready])` tab rule show the first tab.
 * The carousels inside the panels are stock Horizon slideshows; they and the NCC page indicators
 * re-measure themselves through their own ResizeObservers when a panel appears.
 */
class NccProductTabs extends HTMLElement {
  connectedCallback() {
    const tabs = this.#tabs;
    const initial = tabs.find((t) => t.getAttribute('aria-selected') === 'true') ?? tabs[0];
    if (initial) this.#select(initial);
    this.dataset.ready = 'true';
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  get #tabs() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
  }

  /** @param {HTMLElement} tab */
  #select(tab, focus = false) {
    for (const t of this.#tabs) {
      const selected = t === tab;
      t.setAttribute('aria-selected', selected ? 'true' : 'false');
      t.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(t.getAttribute('aria-controls') ?? '');
      if (panel) {
        // the section's CSS keeps hidden panels laid out for the crossfade, so inert keeps them out of reach
        panel.hidden = !selected;
        panel.inert = !selected;
      }
    }
    if (focus) tab.focus();
  }

  #onClick = (/** @type {Event} */ event) => {
    const tab = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest('[role="tab"]') : null);
    if (tab && this.contains(tab)) this.#select(tab);
  };

  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (!(event.target instanceof Element) || !event.target.matches('[role="tab"]')) return;
    const tabs = this.#tabs;
    const index = tabs.indexOf(/** @type {HTMLElement} */ (event.target));
    if (index < 0) return;
    let next = -1;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next < 0) return;
    event.preventDefault();
    this.#select(tabs[next], true);
  };
}

if (!customElements.get('ncc-product-tabs')) {
  customElements.define('ncc-product-tabs', NccProductTabs);
}
